import { createPatch } from "diff";

/**
 * Compute a token-efficient representation of the changes between the
 * AI-generated code and the user's manual edits. Returns either:
 *
 * - A unified diff string (prefixed with "DIFF:") if the changes are
 *   small relative to the full code — this saves 80-95% of tokens
 *   compared to re-sending the full file.
 * - The full edited code (prefixed with "FULL:") if the diff is larger
 *   than ~40% of the original — at that point the diff provides no
 *   token savings and Claude is better off reading the whole thing.
 * - null if the codes are identical (no user edits).
 */
export function computeCodeDiff(
  original: string,
  edited: string,
): string | null {
  if (original === edited) return null;

  const patch = createPatch("component.tsx", original, edited, "AI version", "User edited", {
    context: 3,
  });

  // The patch includes a header (~5 lines). If the patch body is shorter
  // than 40% of the full file, the diff is worth sending. Otherwise the
  // user rewrote most of the file and the full code is cleaner.
  const fullLen = edited.length;
  const diffLen = patch.length;

  if (diffLen < fullLen * 0.6) {
    return `DIFF:${patch}`;
  }

  return `FULL:${edited}`;
}

/**
 * Parse a diff payload back into its components for the backend prompt.
 * Returns { type: "diff", content } or { type: "full", content }.
 */
export function parseDiffPayload(payload: string): {
  type: "diff" | "full";
  content: string;
} {
  if (payload.startsWith("DIFF:")) {
    return { type: "diff", content: payload.slice(5) };
  }
  // Default to full code (backwards compat or explicit FULL: prefix)
  const content = payload.startsWith("FULL:") ? payload.slice(5) : payload;
  return { type: "full", content };
}
